'use client'

import * as React from 'react'
import { motion, type TargetAndTransition, type Transition, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'

function Orb({
  style,
  animate,
  transition,
}: {
  style: React.CSSProperties
  animate: TargetAndTransition
  transition: Transition
}) {
  const shouldReduce = useReducedMotion()
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={style}
      animate={shouldReduce ? {} : animate}
      transition={shouldReduce ? { duration: 0 } : transition}
    />
  )
}

type Particle = { id: number; cx: number; cy: number; r: number; dur: number; delay: number; opacity: number }

function ParticleField({ count = 180 }: { count?: number }) {
  const shouldReduce = useReducedMotion()
  const [particles, setParticles] = React.useState<Particle[]>([])

  React.useEffect(() => {
    setParticles(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        cx: Math.random() * 100,
        cy: Math.random() * 100,
        r: Math.random() < 0.7 ? 1 : 1.5,
        dur: 2.5 + Math.random() * 4,
        delay: Math.random() * 5,
        opacity: 0.15 + Math.random() * 0.55,
      })),
    )
  }, [count])

  if (particles.length === 0) return null

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {particles.map((p) => (
        <circle key={p.id} cx={`${p.cx}%`} cy={`${p.cy}%`} r={p.r} fill="rgba(200,197,245,0.9)">
          {!shouldReduce && (
            <animate
              attributeName="opacity"
              values={`${p.opacity};${Math.min(p.opacity + 0.35, 1)};${p.opacity * 0.3};${p.opacity}`}
              dur={`${p.dur}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
            />
          )}
        </circle>
      ))}
    </svg>
  )
}

export type AuroraBackgroundProps = React.ComponentProps<'div'>

export function AuroraBackground({ children, className, ...props }: AuroraBackgroundProps) {
  return (
    /* aria-hidden : purement décoratif, ignoré par les lecteurs d'écran */
    <div
      data-slot="aurora-background"
      aria-hidden="true"
      className={cn('relative size-full overflow-hidden', className)}
      {...props}
    >
      <div
        className="absolute inset-x-0 top-0 h-full pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(127,119,221,0.13) 0%, transparent 70%)',
        }}
      />

      <Orb
        style={{
          width: 700, height: 700, top: -260, left: -180,
          background: 'radial-gradient(circle at center, rgba(127,119,221,0.28) 0%, transparent 68%)',
          filter: 'blur(55px)',
        }}
        animate={{ x: [0, 70, 20, 0], y: [0, 50, -20, 0], scale: [1, 1.08, 0.96, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />

      <Orb
        style={{
          width: 520, height: 520, top: -140, right: -100,
          background: 'radial-gradient(circle at center, rgba(99,93,200,0.22) 0%, transparent 68%)',
          filter: 'blur(65px)',
        }}
        animate={{ x: [0, -55, 0], y: [0, 65, 0], scale: [1, 1.12, 1] }}
        transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
      />

      <Orb
        style={{
          width: 450, height: 450, top: '25%', left: '35%',
          background: 'radial-gradient(circle at center, rgba(160,155,235,0.14) 0%, transparent 70%)',
          filter: 'blur(70px)',
        }}
        animate={{ x: [0, 40, -30, 0], y: [0, -35, 25, 0], scale: [1, 1.18, 0.92, 1] }}
        transition={{ duration: 35, repeat: Infinity, ease: 'easeInOut', delay: 10 }}
      />

      <ParticleField count={200} />

      {children}
    </div>
  )
}
